import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Banknote, CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react";

type AcqInvoiceNextStepProps = {
  status: string;
  matching: boolean;
  markPaidLoading: boolean;
  onMatch: () => void;
  onMarkPaid: () => void;
  onCancel: () => void;
  onReverse: () => void;
};

export function AcqInvoiceNextStep({
  status,
  matching,
  markPaidLoading,
  onMatch,
  onMarkPaid,
  onCancel,
  onReverse,
}: AcqInvoiceNextStepProps) {
  if (status === "DRAFT") {
    return (
      <NextStepBar
        message="Match to post expenditure to funds, or cancel this draft."
        actions={
          <>
            <Button onClick={onMatch} disabled={matching}>
              {matching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Match invoice
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={onCancel}
              disabled={matching}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel invoice
            </Button>
          </>
        }
      />
    );
  }

  if (status === "MATCHED") {
    return (
      <NextStepBar
        message="Expenditure is recorded. Mark paid when the vendor is paid, or reverse if the match was wrong."
        actions={
          <>
            <Button onClick={onMarkPaid} disabled={markPaidLoading}>
              {markPaidLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Banknote className="mr-2 h-4 w-4" />
              )}
              Mark as paid
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-amber-200 text-amber-900 hover:bg-amber-50"
              onClick={onReverse}
              disabled={markPaidLoading}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reverse match
            </Button>
          </>
        }
      />
    );
  }

  if (status === "PAID") {
    return <NextStepBar message="This invoice is paid. No further actions are available." />;
  }

  if (status === "CANCELLED") {
    return <NextStepBar message="This draft was cancelled. No further actions are available." />;
  }

  return null;
}

function NextStepBar({ message, actions }: { message: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-600">{message}</p>
      {actions ? <div className="flex flex-wrap gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}
